import { proxy } from 'valtio';
import { jwtDecode } from 'jwt-decode';
import { client } from '../../app/services/apollo';
import { REFRESH_TOKENS, GET_CURRENT_USER } from '../../app/services/authService/queries';
import { toast } from 'react-toastify';
import { IUser } from '../../__generated__/graphql';
import { AuthDebugger } from '../../utils/authDebugger';
import { roleActions } from '../roleStore';

interface AuthState {
  tokens: {
    accessToken: string | null;
    idToken: string | null;
    refreshToken: string | null;
  };
  user: Partial<IUser> | null;
  isAuthenticated: boolean;
}

export const authStore = proxy<AuthState>({
  tokens: {
    accessToken: null,
    idToken: null,
    refreshToken: null,
  },
  user: null,
  isAuthenticated: false,
});

export const authActions = {
  setAuth: (
    tokens: { accessToken: string; idToken: string; refreshToken: string },
    user: AuthState['user'],
    source?: string,
  ) => {
    // 调试：记录认证设置
    AuthDebugger.logAuthChange(source || 'Unknown', 'setAuth', { tokens, user });

    // 验证用户对象
    const isValidUser = AuthDebugger.validateUserObject(user, source || 'setAuth');

    if (!isValidUser) {
    }

    // 分析tokens
    if (tokens.accessToken) {
      AuthDebugger.analyzeToken(tokens.accessToken, 'Access');
    }
    if (tokens.idToken) {
      AuthDebugger.analyzeToken(tokens.idToken, 'ID');
    }

    // 检查是否存在冲突
    if (
      authStore.isAuthenticated &&
      authStore.user?.email &&
      user?.email &&
      authStore.user.email !== user.email
    ) {
      AuthDebugger.logConflict(
        { currentUser: authStore.user, source: 'existing' },
        { newUser: user, source: source || 'new' },
      );
    }

    // If this is an email/password login, clear any OIDC tokens from localStorage
    // to prevent OIDC from taking over on page refresh
    if (source === 'EMAIL_SIGNIN' || source === 'GraphQL') {
      const oidcKeys = Object.keys(localStorage).filter(
        key => key.startsWith('oidc.') || (key.includes('cognito') && !key.includes('tokens')),
      );

      if (oidcKeys.length > 0) {
        oidcKeys.forEach(key => localStorage.removeItem(key));
      }
    }

    authStore.tokens = tokens;
    authStore.user = user;
    authStore.isAuthenticated = true;
    localStorage.setItem('tokens', JSON.stringify(tokens));
    // ✅ Also save user to localStorage for page refresh recovery
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    // 传统日志保持兼容性

    AuthDebugger.logDecision('AUTH_SET', `Authentication set from ${source || 'unknown source'}`, {
      userEmail: user?.email,
      hasValidUser: isValidUser,
      tokenCount: Object.values(tokens).filter(Boolean).length,
    });
  },

  restoreTokens: async (): Promise<boolean> => {
    try {
      const { accessToken, refreshToken, idToken } = authStore.tokens;

      AuthDebugger.logAuthChange('AuthStore', 'restoreTokens', {
        hasRefreshToken: !!refreshToken,
        hasAccessToken: !!accessToken,
      });

      const isExpired = (token: string | null) => {
        if (!token) return true;
        try {
          const decoded = jwtDecode<{ exp: number }>(token);
          return decoded.exp * 1000 <= Date.now();
        } catch {
          return true;
        }
      };

      if (isExpired(accessToken) && refreshToken) {
        const { data } = await client.mutate({
          mutation: REFRESH_TOKENS,
          variables: { refreshToken },
          context: {
            headers: {
              'id-token': idToken || '',
              'refresh-token': refreshToken,
            },
          },
        });

        if (!data?.refreshTokens) {
          await authActions.logout();
          return false;
        }

        const newTokens = {
          accessToken: data.refreshTokens.accessToken,
          idToken: data.refreshTokens.idToken,
          refreshToken: data.refreshTokens.refreshToken,
        };
        authStore.tokens = newTokens;
        localStorage.setItem('tokens', JSON.stringify(newTokens));
      }

      const { data } = await client.query({
        query: GET_CURRENT_USER,
        fetchPolicy: 'network-only',
      });

      if (data?.getCurrentUser) {
        AuthDebugger.validateUserObject(data.getCurrentUser, 'restoreTokens');
        authStore.user = data.getCurrentUser;
        authStore.isAuthenticated = true;
        return true;
      }
      return false;
    } catch (error) {
      AuthDebugger.logAuthChange('AuthStore', 'restoreTokens_error', { error });
      await authActions.logout();
      return false;
    }
  },

  initAuth: async () => {
    const storedTokens = localStorage.getItem('tokens');
    const storedUser = localStorage.getItem('user');
    AuthDebugger.logAuthChange('AuthStore', 'initAuth', {
      hasStoredTokens: !!storedTokens,
      hasStoredUser: !!storedUser,
    });

    if (storedTokens) {
      try {
        const tokens = JSON.parse(storedTokens);
        if (!tokens.accessToken || !tokens.refreshToken) {
          localStorage.removeItem('tokens');
          localStorage.removeItem('user');
          return;
        }
        authStore.tokens = tokens;

        // ✅ Restore user from localStorage if available
        // This prevents race condition where AuthBridge checks before user is fetched
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            authStore.user = user;
            authStore.isAuthenticated = true;
          } catch (e) {}
        }

        await authActions.restoreTokens();
      } catch {
        localStorage.removeItem('tokens');
        localStorage.removeItem('user');
      }
    }
  },

  clearTokens: () => {
    AuthDebugger.logAuthChange('AuthStore', 'clearTokens', {});
    authStore.tokens = { accessToken: '', refreshToken: '', idToken: '' };
  },

  logout: async () => {
    AuthDebugger.logAuthChange('AuthStore', 'logout', {
      wasAuthenticated: authStore.isAuthenticated,
      userEmail: authStore.user?.email,
    });

    authStore.tokens = {
      accessToken: null,
      idToken: null,
      refreshToken: null,
    };
    authStore.user = null;
    authStore.isAuthenticated = false;
    localStorage.removeItem('tokens');
    localStorage.removeItem('user');

    // Reset role to guest on logout - logged out users should always be in guest mode
    roleActions.resetRole();

    AuthDebugger.logDecision('LOGOUT', 'User logged out', {});
  },

  setUser: (user: AuthState['user']) => {
    AuthDebugger.logAuthChange('AuthStore', 'setUser', { user });
    AuthDebugger.validateUserObject(user, 'setUser');
    authStore.user = user;
  },
};
